<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxPopover } from '@ecx/ui'
import { DocumentTextIcon } from '@ecx/ui/icons'

const props = withDefaults(
  defineProps<{
    label: string
    appearance?: 'default' | 'compact'
  }>(),
  {
    appearance: 'default',
  },
)

const emit = defineEmits<{
  (e: 'download', format: 'xlsx' | 'csv'): void
}>()

const { t } = useI18n()

const isCompact = computed(() => props.appearance === 'compact')

const triggerClass = computed(() =>
  isCompact.value
    ? 'flex h-7 items-center gap-2 rounded-md border border-grey-100 bg-white py-1.5 pl-2 pr-3 text-grey-950 transition-colors hover:bg-grey-50'
    : 'flex h-10 items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 text-royal-500 transition-colors hover:bg-grey-50',
)

const iconClass = computed(() =>
  isCompact.value ? 'h-4 w-4 shrink-0 text-grey-950' : 'w-5 h-5 shrink-0 text-royal-500',
)

const labelClass = computed(() =>
  isCompact.value
    ? 'whitespace-nowrap text-xs text-grey-950'
    : 'text-sm font-medium text-royal-500',
)

function pick(format: 'xlsx' | 'csv', close: () => void) {
  emit('download', format)
  close()
}
</script>

<template>
  <EcxPopover position="top" align="end" width="w-44" content-class="!p-0">
    <template #trigger="{ isOpen }">
      <button type="button" data-testid="report-download-trigger" :class="triggerClass">
        <DocumentTextIcon :class="iconClass" aria-hidden="true" />
        <span :class="labelClass">{{ label }}</span>
        <svg
          class="h-4 w-4 shrink-0 text-grey-600 transition-transform"
          :class="{ 'rotate-180': isOpen }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </template>
    <template #default="{ close }">
      <div class="flex flex-col gap-1 p-1">
        <button
          type="button"
          data-testid="report-download-xlsx"
          class="flex h-10 w-full items-center rounded-lg px-3 text-left text-sm text-grey-950 transition-colors hover:bg-grey-100"
          @click="pick('xlsx', close)"
        >
          {{ t('corp.bulk_import.report_download.xlsx') }}
        </button>
        <button
          type="button"
          data-testid="report-download-csv"
          class="flex h-10 w-full items-center rounded-lg px-3 text-left text-sm text-grey-950 transition-colors hover:bg-grey-100"
          @click="pick('csv', close)"
        >
          {{ t('corp.bulk_import.report_download.csv') }}
        </button>
      </div>
    </template>
  </EcxPopover>
</template>
